import { Component, Inject, OnInit } from '@angular/core';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { RequestService } from 'src/app/services/request.service';
import { SnackService } from 'src/app/services/snack.service';
import { Project } from 'src/app/model/project/project';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { Adjustment } from 'src/app/model/project/adjustment';
import ProjectHelper from 'src/app/helpers/project-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import EmployeeHelper from 'src/app/helpers/employee-helper';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import { PersonNamePipe } from 'src/app/shared/pipes/person-name.pipe';
import GoogleMapsHelper from 'src/app/helpers/google-maps-helper';
import Address from 'src/app/model/project/address';
import { Lightbox } from 'ngx-lightbox';
import { DatePipe } from '@angular/common';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import Board from 'src/app/model/product/board/board';
import { PlacementTypeChoices } from 'src/app/model/product/board/board-part';
import Product, {
  HEIGHT_EQUIPMENT_OPTIONS,
} from 'src/app/model/product/product';
import Windowsticker from 'src/app/model/product/windowsticker/windowsticker';
import WindowStickerPlacement from 'src/app/model/product/windowsticker/windowsticker-placement';
import BoardPlacement from 'src/app/model/product/board/board-placement';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import OtherProductPlacement from 'src/app/model/product/other-product/other-product-placement';
import {
  LightboxItem,
  LocationVerificationProjectWideDisplay,
} from './location-verification-project-wide-display';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import Employee from 'src/app/model/product/employee';
import ErrorHelper, { VerificationError } from 'src/app/helpers/error-helper';
import ProductAsset from 'src/app/model/product-asset';
import { GetStreetViewMetadataService } from 'src/app/services/google-maps/get-streetview-metadata.service';
import {
  GoogleGeoCode,
  MapsService,
} from '../../services/google-maps/maps.service';

@Component({
  selector: 'location-verification-dialog',
  templateUrl: './location-verification-dialog.component.html',
  styleUrls: ['./location-verification-dialog.component.scss'],
  providers: [FormatPhoneNumber, PersonNamePipe, DatePipe],
})
export class LocationVerificationDialogComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  projectId: number;
  private subProjectId: number;

  locationVerificationProjectWideDisplay: LocationVerificationProjectWideDisplay;

  init: boolean = false;
  submitting: boolean = false;

  bannerTitle: string;
  bannerContainerClass: string;

  quotationDateLabel: string;
  quotationDate: Date;
  contactDisplays: ContactDisplay[] = [];
  adjustmentHeightEquipmentLines: LineWithHeader[];
  requestedDateDisplay: string = '';

  multipleProducts: boolean;

  placerChoices: Employee[] = [];
  externalSupplierChoices: Employee[] = [];
  heightEquipmentOptions = HEIGHT_EQUIPMENT_OPTIONS;

  adjustmentInternalDesignAsset: ProductAsset;
  adjustmentInternalDesignLightboxItems: LightboxItem[];
  adjustmentExternalDesignAsset: ProductAsset;
  adjustmentPlacerNames: string;

  private static readonly VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR = 100;

  constructor(
    public dialogRef: MatDialogRef<LocationVerificationDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
    private mapsService: MapsService,
    private requestService: RequestService,
    private snackService: SnackService,
    private employeeChoicesService: EmployeeChoicesService,
    private getStreetViewMetadataService: GetStreetViewMetadataService,
    private formatPhoneNumber: FormatPhoneNumber,
    private personNamePipe: PersonNamePipe,
    private _lightbox: Lightbox
  ) {
    super();
    this.projectId = data.projectId;
    this.subProjectId = data.subProjectId;
  }

  ngOnInit() {
    if (this.projectId == null) {
      this.snackService.showError('projectId was null');
      return;
    }
    if (this.subProjectId == null) {
      this.snackService.showError('subProjectId was null');
      return;
    }

    this.locationVerificationProjectWideDisplay = {
      showNearbyParkingQuestion: false,
      showFloorQuestion: false,
      hasAddress: false,
    };
    this.addSubscription(
      this.requestService.getNormal(`/projects/${this.projectId}`),
      (res: Project) => {
        this.locationVerificationProjectWideDisplay.project = res;
        this.locationVerificationProjectWideDisplay.subProject =
          !CollectionHelper.isPopulated(
            this.locationVerificationProjectWideDisplay.project.subProjects
          )
            ? null
            : this.locationVerificationProjectWideDisplay.project.subProjects.find(
                (sp) => sp.id === this.subProjectId
              );
        if (this.locationVerificationProjectWideDisplay.subProject == null) {
          this.snackService.showError(
            `Kon subproject met id ${this.subProjectId} niet vinden`
          );
          return;
        }
        if (
          this.locationVerificationProjectWideDisplay.subProject.type !==
          'BaseProject'
        ) {
          this.addSubscription(
            this.employeeChoicesService.getExternalSupplierChoicesSubscribable(),
            (externalSupplierChoices: Employee[]) => {
              this.externalSupplierChoices = externalSupplierChoices;
            }
          );
        }
        this.locationVerificationProjectWideDisplay.baseProject =
          ProjectHelper.getBaseProject(
            this.locationVerificationProjectWideDisplay.project
          );
        this.preprocessSubproject();
        this.initializeBanner();
        this.init = true;
      }
    );
    this.employeeChoicesService.updatePlacerChoices();
    this.employeeChoicesService.updateExternalSupplierChoices();
    this.addSubscription(
      this.employeeChoicesService.getPlacerChoicesSubscribable(),
      (placers: Employee[]) => {
        this.placerChoices = placers;
      }
    );
  }

  initializeBanner(): void {
    this.bannerTitle = ProjectHelper.getFormDisplayNameUpperCase(
      this.locationVerificationProjectWideDisplay.subProject
    );
    this.bannerTitle =
      this.bannerTitle === 'Project' ? 'Te plaatsen' : this.bannerTitle;
    this.bannerContainerClass = ProjectHelper.getTypeStatusString(
      this.locationVerificationProjectWideDisplay.subProject
    );
  }

  preprocessSubproject() {
    if (
      this.locationVerificationProjectWideDisplay.subProject.type ===
      'BaseProject'
    ) {
      this.preprocessBaseProject();
    } else {
      this.preprocessAdjustment();
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.needsQuotation &&
      this.locationVerificationProjectWideDisplay.subProject.quotation != null
    ) {
      if (
        this.locationVerificationProjectWideDisplay.subProject.quotation
          .approved &&
        this.locationVerificationProjectWideDisplay.subProject.quotation
          .approvalRegistration != null
      ) {
        this.quotationDateLabel = 'Datum goedgekeurd';
        this.quotationDate =
          this.locationVerificationProjectWideDisplay.subProject.quotation.approvalRegistration.date;
      } else if (
        this.locationVerificationProjectWideDisplay.subProject.quotationSent &&
        this.locationVerificationProjectWideDisplay.subProject.quotation
          .sentRegistration != null
      ) {
        this.quotationDateLabel = 'Datum verstuurd';
        this.quotationDate =
          this.locationVerificationProjectWideDisplay.subProject.quotation.sentRegistration.date;
      } else {
        this.quotationDateLabel = 'Datum verstuurd';
        this.quotationDate = null;
      }
    }
    this.preprocessContacts();
    this.preprocessAddress();
    this.preprocessAssets();
    this.calculateShowUndergroundPipeworkQuestion();
    this.locationVerificationProjectWideDisplay.showTopdownLocationPhoto =
      CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.project.products
      ) &&
      this.locationVerificationProjectWideDisplay.project.products.some(
        (p) => ProductHelper.isBoard(p) || ProductHelper.isOtherProduct(p)
      );
    this.locationVerificationProjectWideDisplay.sitePhotosTitle = this
      .locationVerificationProjectWideDisplay.showTopdownLocationPhoto
      ? 'Afbeelding locatie (vooraanzicht)'
      : 'Afbeelding locatie';
  }

  preprocessBaseProject() {
    this.multipleProducts =
      this.locationVerificationProjectWideDisplay.project.products != null &&
      this.locationVerificationProjectWideDisplay.project.products.length > 1;
    if (
      CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.subProject.projectInvoices
      )
    ) {
      this.locationVerificationProjectWideDisplay.subProject.projectInvoices.forEach(
        (i) =>
          (i.displayPrice = ProjectHelper.getSubtotalOfInvoice(
            i,
            this.locationVerificationProjectWideDisplay.project
          ))
      );
    }
    this.locationVerificationProjectWideDisplay.kilometersTo =
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement ==
      null
        ? null
        : this.locationVerificationProjectWideDisplay.subProject
            .projectPlacement.kilometersTo;
    this.calculateNearbyParking();
    this.locationVerificationProjectWideDisplay.showFloorQuestion =
      this.calculateShowFloorQuestion();
  }

  preprocessAdjustment() {
    const adjustment = <Adjustment>(
      this.locationVerificationProjectWideDisplay.subProject
    );
    if (CollectionHelper.isPopulated(adjustment.projectInvoices)) {
      adjustment.projectInvoices.forEach(
        (i) => (i.displayPrice = ProjectHelper.getAdjustmentInvoiceSubTotal(i))
      );
    }
    if (
      this.locationVerificationProjectWideDisplay.baseProject != null &&
      this.locationVerificationProjectWideDisplay.baseProject
        .projectPlacement != null
    ) {
      this.locationVerificationProjectWideDisplay.kilometersTo =
        this.locationVerificationProjectWideDisplay.baseProject.projectPlacement.kilometersTo;
      this.locationVerificationProjectWideDisplay.baseProjectPlacementNotes =
        this.locationVerificationProjectWideDisplay.baseProject.projectPlacement.extraPlacementInfo;
    }
    if (
      CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.project.products
      )
    ) {
      this.adjustmentHeightEquipmentLines = [];
      this.locationVerificationProjectWideDisplay.project.products.forEach(
        (p, i) => {
          if (
            p.productPlacement != null &&
            p.productPlacement.heightEquipment != null
          ) {
            this.adjustmentHeightEquipmentLines.push({
              header: `${ProductHelper.getTypeOf(p)} #${i + 1}`,
              line: p.productPlacement.heightEquipment.name,
            });
          }
        }
      );
    }
    this.calculateAdjustmentPlacersNames();
  }

  preprocessContacts() {
    if (
      CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.project.projectContacts
      )
    ) {
      this.locationVerificationProjectWideDisplay.project.projectContacts.forEach(
        (pc) => {
          let phoneNumber = null;
          let mobileNumber = null;
          if (CollectionHelper.isPopulated(pc.contact.telephones)) {
            for (const telephone of pc.contact.telephones) {
              if (
                telephone.type === 'phone' &&
                !StringHelper.isEmpty(telephone.number) &&
                StringHelper.isEmpty(phoneNumber)
              ) {
                phoneNumber =
                  this.formatPhoneNumber.transformTelephone(telephone);
              }
              if (
                telephone.type === 'mobile' &&
                !StringHelper.isEmpty(telephone.number) &&
                StringHelper.isEmpty(mobileNumber)
              ) {
                mobileNumber =
                  this.formatPhoneNumber.transformTelephone(telephone);
              }
              if (
                !(
                  StringHelper.isEmpty(phoneNumber) ||
                  StringHelper.isEmpty(mobileNumber)
                )
              ) {
                break;
              }
            }
          }
          const email = CollectionHelper.isPopulated(pc.contact.emails)
            ? pc.contact.emails[0].email
            : null;
          this.contactDisplays.push({
            company: pc.company.regularName,
            firstName: pc.contact.firstName,
            fullName: this.personNamePipe.transform(
              pc.contact.firstName,
              pc.contact.lastName
            ),
            phoneNumber: phoneNumber,
            mobileNumber: mobileNumber,
            email: email,
          });
        }
      );
    }
  }

  preprocessAddress() {
    this.locationVerificationProjectWideDisplay.hasAddress =
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement !=
        null &&
      !(
        this.locationVerificationProjectWideDisplay.subProject.projectPlacement
          .address == null ||
        StringHelper.isEmpty(
          this.locationVerificationProjectWideDisplay.subProject
            .projectPlacement.address.line1
        ) ||
        StringHelper.isEmpty(
          this.locationVerificationProjectWideDisplay.subProject
            .projectPlacement.address.city
        ) ||
        StringHelper.isEmpty(
          this.locationVerificationProjectWideDisplay.subProject
            .projectPlacement.address.postalCode
        )
      );
    if (this.locationVerificationProjectWideDisplay.hasAddress) {
      if (
        this.locationVerificationProjectWideDisplay.subProject.projectPlacement
          .address.coordinates != null
      ) {
        this.locationVerificationProjectWideDisplay.coordinates = {
          lat: this.locationVerificationProjectWideDisplay.subProject
            .projectPlacement.address.coordinates.lat,
          lng: this.locationVerificationProjectWideDisplay.subProject
            .projectPlacement.address.coordinates.lng,
        };
      } else {
        this.addSubscriptionSwitchMap(
          this.mapsService.getGeocode(
            GoogleMapsHelper.constructRequestAddress(
              this.locationVerificationProjectWideDisplay.subProject
                .projectPlacement.address
            )
          ),
          (googleGeoCode: GoogleGeoCode) => {
            if (googleGeoCode) {
              this.locationVerificationProjectWideDisplay.coordinates = {
                lat: googleGeoCode.coordinates.lat,
                lng: googleGeoCode.coordinates.lng,
              };
              this.locationVerificationProjectWideDisplay.subProject.projectPlacement.address.coordinates =
                googleGeoCode.coordinates;
              this.requestService.put(
                this.locationVerificationProjectWideDisplay.subProject
                  .projectPlacement.address,
                '/addresses'
              );
            }
          },
          (res: Address) => {
            if (res.coordinates != null) {
              this.locationVerificationProjectWideDisplay.subProject.projectPlacement.address.coordinates.id =
                res.coordinates.id;
            }
          }
        );
      }
    }
    this.getStreetViewMetadataService
      .getStreetViewLink(
        this.locationVerificationProjectWideDisplay.subProject.projectPlacement
          .address
      )
      .then(
        (panoId: string) =>
          (this.locationVerificationProjectWideDisplay.panoId = panoId)
      )
      .catch((err) => console.error(err));
  }

  preprocessAssets() {
    this.locationVerificationProjectWideDisplay.locationAsset = null;
    this.locationVerificationProjectWideDisplay.topdownLocationAsset = null;
    if (
      this.locationVerificationProjectWideDisplay.baseProject != null &&
      this.locationVerificationProjectWideDisplay.baseProject
        .projectPlacement != null &&
      CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.baseProject.projectPlacement
          .projectPlacementAssets
      )
    ) {
      if (this.locationVerificationProjectWideDisplay.locationAlbum == null) {
        this.locationVerificationProjectWideDisplay.locationAlbum = [];
      } else {
        this.locationVerificationProjectWideDisplay.locationAlbum.length = 0;
      }
      if (
        this.locationVerificationProjectWideDisplay.topdownLocationAlbum == null
      ) {
        this.locationVerificationProjectWideDisplay.topdownLocationAlbum = [];
      } else {
        this.locationVerificationProjectWideDisplay.topdownLocationAlbum.length = 0;
      }
      this.locationVerificationProjectWideDisplay.baseProject.projectPlacement.projectPlacementAssets.forEach(
        (pa) => {
          if (!pa.archived && pa.asset != null) {
            if (pa.formSection === 'sitePhotos') {
              this.locationVerificationProjectWideDisplay.locationAsset =
                this.locationVerificationProjectWideDisplay.locationAsset ==
                null
                  ? pa
                  : this.locationVerificationProjectWideDisplay.locationAsset;
              this.locationVerificationProjectWideDisplay.locationAlbum.push({
                src: `/api/assets/${pa.asset.uuid}`,
                caption: '',
                thumb: `/api/assets/${pa.asset.uuid}`,
              });
            } else if (pa.formSection === 'locationTopdown') {
              this.locationVerificationProjectWideDisplay.topdownLocationAsset =
                this.locationVerificationProjectWideDisplay
                  .topdownLocationAsset == null
                  ? pa
                  : this.locationVerificationProjectWideDisplay
                      .topdownLocationAsset;
              this.locationVerificationProjectWideDisplay.topdownLocationAlbum.push(
                {
                  src: `/api/assets/${pa.asset.uuid}`,
                  caption: '',
                  thumb: `/api/assets/${pa.asset.uuid}`,
                }
              );
            }
          }
        }
      );
    }
  }

  placeInstructionsChanged(value: string) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.placeInstructions =
      value;
  }

  inpressLogoChanged(value: boolean) {
    (<Adjustment>(
      this.locationVerificationProjectWideDisplay.subProject
    )).inpressLogo = value;
  }

  immoscoopLogoChanged(value: boolean) {
    (<Adjustment>(
      this.locationVerificationProjectWideDisplay.subProject
    )).immoscoopLogo = value;
  }

  spottoLogoChanged(value: boolean) {
    (<Adjustment>(
      this.locationVerificationProjectWideDisplay.subProject
    )).spottoLogo = value;
  }

  soldRentedStickerWorkHeightChange(value: number) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.soldRentedStickerWorkHeight =
      value;
  }

  obstaclesPresentChange(value: boolean) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.obstaclesPresent =
      value;
  }

  obstaclesInfoTextChange(value: string) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.obstaclesInfo =
      value;
  }

  placerChange() {
    this.calculateAdjustmentPlacersNames();
  }

  currentConfirmedPlacementDateChange(event) {
    this.locationVerificationProjectWideDisplay.subProject.currentConfirmedPlacementDate =
      event.value;
  }

  currentPlacementDateConfirmedChange(value) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.currentPlacementDateConfirmed =
      value;
  }

  installerHourBudgetChange(value: number) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.installerHourBudget =
      value;
  }

  installerMinuteBudgetChange(value: number) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.installerMinuteBudget =
      value;
  }

  locationVerifiedChanged(value: boolean) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.locationVerified =
      value;
    if (!value) {
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement.locationVerificationCheckedByExpert =
        false;
    }
  }

  locationVerificationCheckedByExpertChanged(value: boolean) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.locationVerificationCheckedByExpert =
      value;
  }

  locationVerificationExtraInfoChanged(value: string) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.locationVerificationExtraInfo =
      value;
  }

  openLocationLightbox() {
    this._lightbox.open(
      this.locationVerificationProjectWideDisplay.locationAlbum
    );
  }

  closeDialog() {
    this.dialogRef.close(null);
  }

  save() {
    this.submitting = true;
    this.addSubscription(
      this.requestService.put(
        this.locationVerificationProjectWideDisplay.project,
        '/projects'
      ),
      (res: Project) => {
        this.submitting = false;
        this.dialogRef.close(true);
      },
      (err) => {
        this.snackService.showError(err);
        this.submitting = false;
      }
    );
  }

  calculateShowUndergroundPipeworkQuestion() {
    if (
      this.locationVerificationProjectWideDisplay.project == null ||
      !CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.project.products
      )
    ) {
      this.locationVerificationProjectWideDisplay.showUndergroundPipeworkQuestion =
        false;
      return;
    }
    this.locationVerificationProjectWideDisplay.showUndergroundPipeworkQuestion =
      this.locationVerificationProjectWideDisplay.project.products.some((p) => {
        if (ProductHelper.isBoard(p)) {
          const board = <Board>p;
          return (
            CollectionHelper.isPopulated(board.boardParts) &&
            board.boardParts.some(
              (bp) =>
                bp.placementType != null &&
                bp.placementType.ordinal ===
                  PlacementTypeChoices.ON_POSTS.ordinal
            )
          );
        }
        return false;
      });
  }

  calculateNearbyParking() {
    if (
      !CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.project.products
      )
    ) {
      return;
    }
    for (const product of this.locationVerificationProjectWideDisplay.project
      .products) {
      if (ProductHelper.isBoard(product)) {
        const board = <Board>product;
        if (!CollectionHelper.isPopulated(board.boardParts)) {
          continue;
        }
        const placement = <BoardPlacement>board.productPlacement;
        for (const part of board.boardParts) {
          if (
            part.placementType != null &&
            part.placementType.ordinal ===
              PlacementTypeChoices.ON_STONES.ordinal
          ) {
            this.locationVerificationProjectWideDisplay.showNearbyParkingQuestion =
              true;
            this.locationVerificationProjectWideDisplay.nearbyParkingValue =
              this.locationVerificationProjectWideDisplay.nearbyParkingValue ===
              true
                ? this.locationVerificationProjectWideDisplay.nearbyParkingValue
                : this.locationVerificationProjectWideDisplay
                    .nearbyParkingValue === false
                ? placement.canParkNearby
                  ? true
                  : this.locationVerificationProjectWideDisplay
                      .nearbyParkingValue
                : placement.canParkNearby;
            this.locationVerificationProjectWideDisplay.nearbyParkingInfo =
              StringHelper.isEmpty(
                this.locationVerificationProjectWideDisplay.nearbyParkingInfo
              )
                ? placement.nearbyParkingInfo
                : this.locationVerificationProjectWideDisplay.nearbyParkingInfo;
          }
        }
      }
    }
  }

  calculateShowFloorQuestion(): boolean {
    return (
      !CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.project.products
      ) ||
      !this.locationVerificationProjectWideDisplay.project.products.every(
        (p) => {
          return (
            ProductHelper.isBoard(p) &&
            (<Board>p).boardParts != null &&
            (<Board>p).boardParts.every(
              (bp) =>
                bp.placementType != null &&
                bp.placementType.ordinal ===
                  PlacementTypeChoices.ON_POSTS.ordinal
            )
          );
        }
      )
    );
  }

  calculateAdjustmentPlacersNames(): void {
    if (
      !CollectionHelper.hasLengthGreaterThan(
        this.locationVerificationProjectWideDisplay.subProject.projectPlacement
          .placers,
        1
      )
    ) {
      this.adjustmentPlacerNames = null;
      return;
    }
    this.adjustmentPlacerNames = StringHelper.join(
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement.placers.map(
        (e: Employee) => EmployeeHelper.getName(e)
      ),
      ', '
    );
  }

  public locationVerifiedValidation(): boolean {
    const error: VerificationError = {};
    if (
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement !=
        null &&
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .appointment
    ) {
      this.appointmentVerification(error);
      if (!StringHelper.isEmpty(error.message)) {
        this.snackService.showError(error.message);
        return false;
      }
      return true;
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.withPlacement &&
      !this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .placeASAP &&
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .placementEarliestDate == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een keuze te maken voor de gevraagde plaatsingsdatum`,
        this.locationVerificationProjectWideDisplay.subProject.type ===
          'BaseProject'
          ? 33
          : 6
      );
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.type ===
      'BaseProject'
    ) {
      this.baseProjectLocationVerifiedValidation(error);
    } else {
      this.adjustmentLocationVerifiedValidation(error);
    }
    if (!StringHelper.isEmpty(error.message)) {
      this.snackService.showError(error.message);
      return false;
    }
    return true;
  }

  private appointmentVerification(error: VerificationError): void {
    if (
      StringHelper.isEmpty(
        this.locationVerificationProjectWideDisplay.subProject.projectPlacement
          .needAppointmentInfo
      )
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de afspraak`,
        this.locationVerificationProjectWideDisplay.subProject.type ===
          'BaseProject'
          ? 0
          : 2
      );
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .collectKey == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of er een sleutel moet worden afgehaald`,
        this.locationVerificationProjectWideDisplay.subProject.type ===
          'BaseProject'
          ? 1
          : 3
      );
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .collectKey &&
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .collectKeyFrom == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven waar de sleutel afgehaald moet worden`,
        this.locationVerificationProjectWideDisplay.subProject.type ===
          'BaseProject'
          ? 2
          : 4
      );
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .collectKey &&
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .collectKeyFrom === -1 &&
      StringHelper.isEmpty(
        this.locationVerificationProjectWideDisplay.subProject.projectPlacement
          .collectKeyExtraInfo
      )
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve in te vullen op welk adres de sleutel af te halen`,
        this.locationVerificationProjectWideDisplay.subProject.type ===
          'BaseProject'
          ? 3
          : 5
      );
    }
  }

  private baseProjectLocationVerifiedValidation(
    error: VerificationError
  ): void {
    if (
      this.locationVerificationProjectWideDisplay.subProject.withPlacement &&
      StringHelper.isEmpty(
        this.locationVerificationProjectWideDisplay.subProject.projectPlacement
          .extraInfoSite
      )
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Extra opmerkingen locatie' in te vullen`,
        0
      );
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.withPlacement &&
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .appointment === null
    ) {
      ErrorHelper.evaluateError(
        error,
        'Gelieve aan te geven of er een afspraak nodig is',
        32
      );
    }
    if (
      CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.project.products
      )
    ) {
      for (
        let i = 0;
        i < this.locationVerificationProjectWideDisplay.project.products.length;
        i++
      ) {
        const product =
          this.locationVerificationProjectWideDisplay.project.products[i];
        const errorLabel = `${ProductHelper.getTypeOf(product)} #${i + 1}`;

        if (
          ProductHelper.isWindowSticker(product) ||
          ProductHelper.isCustomWindowSticker(product)
        ) {
          this.windowStickerLocationVerifiedValidation(
            product,
            i,
            errorLabel,
            error
          );
        } else if (ProductHelper.isBoard(product)) {
          this.boardLocationVerifiedValidation(product, i, errorLabel, error);
        } else if (ProductHelper.isOtherProduct(product)) {
          this.otherProductLocationVerifiedValidation(
            product,
            i,
            errorLabel,
            error
          );
        }
        this.generalProductLocationVerifiedValidation(
          product,
          i,
          errorLabel,
          error
        );
      }
    }
  }

  private windowStickerLocationVerifiedValidation(
    product: Product,
    index: number,
    errorLabel: string,
    error: VerificationError
  ): void {
    if (
      this.locationVerificationProjectWideDisplay.subProject.withPlacement &&
      (!CollectionHelper.isPopulated(product.productAssets) ||
        !product.productAssets.some(
          (pa) => !pa.archived && pa.formSection === 'windowDesign'
        ))
    ) {
      ErrorHelper.evaluateError(
        error,
        `${errorLabel} heeft geen afbeeldingen voor ontwerp + impressie`,
        1 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    const windowSticker = <Windowsticker>product;
    const placement = <WindowStickerPlacement>windowSticker.productPlacement;
    if (placement.workHeight == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Afstand vanop de grond tot bovenkant sticker' in te vullen voor ${errorLabel}`,
        22 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (placement.heightEquipment == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een optie te kiezen voor ladder/stelling/hoogwerker voor ${errorLabel}`,
        23 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (placement.windowObstacle == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of er obstakels zijn voor ${errorLabel}`,
        24 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      placement.windowObstacle &&
      StringHelper.isEmpty(placement.extraInfoWindowObstacle)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de obstakels voor ${errorLabel}`,
        25 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (placement.oldStickerPresent == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of er oude stickers aanwezig zijn voor ${errorLabel}`,
        26 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      placement.oldStickerPresent &&
      StringHelper.isEmpty(placement.extraInfoOldStickerPresent)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de oude stickers voor ${errorLabel}`,
        27 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (StringHelper.isEmpty(placement.alignment)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Uitlijnen van de sticker op het raam' in te vullen voor ${errorLabel}`,
        28 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
  }

  private boardLocationVerifiedValidation(
    product: Product,
    index: number,
    errorLabel: string,
    error: VerificationError
  ): void {
    if (
      this.locationVerificationProjectWideDisplay.subProject.withPlacement &&
      (!CollectionHelper.isPopulated(product.productAssets) ||
        !product.productAssets.some(
          (pa) => !pa.archived && pa.formSection === 'boardDesign'
        ))
    ) {
      ErrorHelper.evaluateError(
        error,
        `${errorLabel} heeft geen afbeeldingen voor ontwerp + impressie`,
        1 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.withPlacement &&
      this.locationVerificationProjectWideDisplay
        .showUndergroundPipeworkQuestion
    ) {
      if (
        this.locationVerificationProjectWideDisplay.subProject.projectPlacement
          .undergroundPipework &&
        StringHelper.isEmpty(
          this.locationVerificationProjectWideDisplay.subProject
            .projectPlacement.undergroundPipeworkText
        )
      ) {
        ErrorHelper.evaluateError(
          error,
          `Gelieve informatie in te vullen over de ondergrondse leidingen`,
          2
        );
      }
    }
    const board = <Board>product;
    const placement = <BoardPlacement>board.productPlacement;
    if (StringHelper.isEmpty(board.placeInstructions)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Hoe plaatsen' in te vullen voor ${errorLabel}`,
        3 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (StringHelper.isEmpty(board.distanceFrontPostToStreet)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Afstand voorste paal tot straat' in te vullen voor ${errorLabel}`,
        22 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (placement.newWoodPosts == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of ${errorLabel} niew hout of recup hout gebruikt`,
        25 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (placement.obstaclesPresent == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of er obstakels zijn voor ${errorLabel}`,
        28 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      placement.obstaclesPresent &&
      StringHelper.isEmpty(placement.obstaclesInfo)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de obstakels voor ${errorLabel}`,
        29 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.withPlacement &&
      this.locationVerificationProjectWideDisplay.showNearbyParkingQuestion
    ) {
      if (
        this.locationVerificationProjectWideDisplay.nearbyParkingValue == null
      ) {
        ErrorHelper.evaluateError(
          error,
          'Gelieve aan te geven of er vlakbij geparkeerd kan worden',
          30 +
            index *
              LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
        );
      }
      if (
        this.locationVerificationProjectWideDisplay.nearbyParkingValue &&
        StringHelper.isEmpty(
          this.locationVerificationProjectWideDisplay.nearbyParkingInfo
        )
      ) {
        ErrorHelper.evaluateError(
          error,
          `Gelieve informatie in te vullen over het vlakbij parkeren`,
          31 +
            index *
              LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
        );
      }
    }
  }

  private otherProductLocationVerifiedValidation(
    product: Product,
    index: number,
    errorLabel: string,
    error: VerificationError
  ): void {
    if (
      this.locationVerificationProjectWideDisplay.subProject.withPlacement &&
      (!CollectionHelper.isPopulated(product.productAssets) ||
        !product.productAssets.some(
          (pa) => !pa.archived && pa.formSection === 'otherDesign'
        ))
    ) {
      ErrorHelper.evaluateError(
        error,
        `${errorLabel} heeft geen afbeeldingen voor ontwerp + impressie`,
        1 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    const otherProduct = <OtherProduct>product;
    const placement = <OtherProductPlacement>otherProduct.productPlacement;
    if (placement.obstaclesPresent == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of er obstakels zijn voor ${errorLabel}`,
        22 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      placement.obstaclesPresent &&
      StringHelper.isEmpty(placement.obstaclesInfo)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de obstakels voor ${errorLabel}`,
        23 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
  }

  private generalProductLocationVerifiedValidation(
    product: Product,
    index: number,
    errorLabel: string,
    error: VerificationError
  ): void {
    if (
      this.locationVerificationProjectWideDisplay.showFloorQuestion &&
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .placementFloor == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een optie te kiezen voor Verdieping`,
        4 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (product.productPlacement.extraHoogwerker == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of een extra hoogwerker nodig is voor ${errorLabel}`,
        5 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.extraHoogwerker &&
      StringHelper.isEmpty(product.productPlacement.extraHoogwerkerDescription)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de extra hoogwerker voor ${errorLabel}`,
        6 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.extraHoogwerker &&
      product.productPlacement.extraHoogwerkerEmployee == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een medewerker te selecteren voor de extra hoogwerker voor ${errorLabel}`,
        7 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.extraHoogwerker &&
      product.productPlacement.extraHoogwerkerDeadline == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een deadline op te geven voor de extra hoogwerker voor ${errorLabel}`,
        8 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (product.productPlacement.parkingBan == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of een parkeerverbod nodig is voor ${errorLabel}`,
        9 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.parkingBan &&
      StringHelper.isEmpty(product.productPlacement.extraInfoParkingBan)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over het parkeerverbod voor ${errorLabel}`,
        10 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.parkingBan &&
      product.productPlacement.parkingBanEmployee == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een medewerker te selecteren voor het parkeerverbod voor ${errorLabel}`,
        11 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.parkingBan &&
      product.productPlacement.parkingBanDeadline == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een deadline op te geven voor het parkeerverbod voor ${errorLabel}`,
        12 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (product.productPlacement.needExtraSupplier == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of een extra leverancier nodig is voor ${errorLabel}`,
        13 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.needExtraSupplier &&
      StringHelper.isEmpty(product.productPlacement.extraInfoNeedExtraSupplier)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de extra leverancier voor ${errorLabel}`,
        14 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.needExtraSupplier &&
      product.productPlacement.extraSupplierEmployee == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een medewerker te selecteren voor de extra leverancier voor ${errorLabel}`,
        15 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.needExtraSupplier &&
      product.productPlacement.extraSupplierDeadline == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een deadline op te geven voor de extra leverancier voor ${errorLabel}`,
        16 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (product.productPlacement.needExtraMaterials == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of extra materialen nodig zijn voor ${errorLabel}}`,
        17 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.needExtraMaterials &&
      StringHelper.isEmpty(product.productPlacement.extraInfoNeedExtraMaterials)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de extra materialen voor ${errorLabel}`,
        18 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.needExtraMaterials &&
      product.productPlacement.extraMaterialsEmployee == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een medewerker te selecteren voor de extra materialen voor ${errorLabel}`,
        19 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.needExtraMaterials &&
      product.productPlacement.extraMaterialsDeadline == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een deadline op te geven voor de extra materialen voor ${errorLabel}`,
        20 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (product.inpressLogo == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of het Inpress logo toegevoegd mag worden voor ${errorLabel}`,
        21 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (product.productPlacement.actualPlacementDate == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een plaatsingsdatum te kiezen voor ${errorLabel}`,
        34 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
    if (
      product.productPlacement.installerHourBudget == null &&
      product.productPlacement.installerMinuteBudget == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve de voorziene tijd voor de installateur in te vullen voor ${errorLabel}`,
        35 +
          index *
            LocationVerificationDialogComponent.VERIFICATION_PRODUCT_INDEX_PRECEDENCE_FACTOR
      );
    }
  }

  private adjustmentLocationVerifiedValidation(error: VerificationError): void {
    const adjustment = <Adjustment>(
      this.locationVerificationProjectWideDisplay.subProject
    );

    if (StringHelper.isEmpty(adjustment.extraInfoAdjustment)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve in het externe gedeelte de omschrijving voor ${ProjectHelper.getFormDisplayArticle(
          adjustment
        )} ${ProjectHelper.getFormDisplayNameLowerCase(
          adjustment
        )} in te vullen`,
        0
      );
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.withPlacement &&
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .appointment === null
    ) {
      ErrorHelper.evaluateError(
        error,
        'Gelieve aan te geven of er een afspraak nodig is',
        1
      );
    }
    if (StringHelper.isEmpty(adjustment.material)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Materiaal' in te vullen`,
        7
      );
    }
    if (StringHelper.isEmpty(adjustment.amountDesign)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Aantal' in te vullen`,
        8
      );
    }
    if (StringHelper.isEmpty(adjustment.size)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Afmetingen' in te vullen`,
        9
      );
    }
    if (StringHelper.isEmpty(adjustment.extraInfoDesign)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Extra info ontwerp' in te vullen`,
        10
      );
    }
    if (StringHelper.isEmpty(adjustment.withPlacementNotes)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Info installateur' in te vullen`,
        11
      );
    }
    if (StringHelper.isEmpty(adjustment.projectPlacement.placeInstructions)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Hoe plaatsen' in te vullen`,
        12
      );
    }
    if (
      !CollectionHelper.isPopulated(
        adjustment.projectPlacement.projectPlacementAssets
      ) ||
      !adjustment.projectPlacement.projectPlacementAssets.some(
        (pa: ProductAsset) =>
          !pa.archived && pa.formSection === 'adjustmentDesign'
      )
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een bestand toe te voegen voor ontwerp`,
        13
      );
    }
    if (
      !CollectionHelper.isPopulated(
        adjustment.projectPlacement.projectPlacementAssets
      ) ||
      !adjustment.projectPlacement.projectPlacementAssets.some(
        (pa: ProductAsset) =>
          !pa.archived && pa.formSection === 'adjustmentInternal'
      )
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een bestand toe te voegen voor ontwerp + impressie`,
        13
      );
    }
    if (adjustment.extraHoogwerker == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of een extra hoogwerker nodig is`,
        14
      );
    }
    if (
      adjustment.extraHoogwerker &&
      StringHelper.isEmpty(adjustment.extraHoogwerkerDescription)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de extra hoogwerker`,
        15
      );
    }
    if (
      adjustment.extraHoogwerker &&
      adjustment.extraHoogwerkerEmployee == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een medewerker te selecteren voor de extra hoogwerker`,
        16
      );
    }
    if (
      adjustment.extraHoogwerker &&
      adjustment.extraHoogwerkerDeadline == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een deadline op te geven voor de extra hoogwerker`,
        17
      );
    }
    if (adjustment.parkingBan == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of een parkeerverbod nodig is`,
        18
      );
    }
    if (
      adjustment.parkingBan &&
      StringHelper.isEmpty(adjustment.extraInfoParkingBan)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over het parkeerverbod`,
        19
      );
    }
    if (adjustment.parkingBan && adjustment.parkingBanEmployee == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een medewerker te selecteren voor het parkeerverbod`,
        20
      );
    }
    if (adjustment.parkingBan && adjustment.parkingBanDeadline == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een deadline op te geven voor het parkeerverbod voor`,
        21
      );
    }
    if (adjustment.needExtraSupplier == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of een extra leverancier nodig is`,
        22
      );
    }
    if (
      adjustment.needExtraSupplier &&
      StringHelper.isEmpty(adjustment.extraInfoNeedExtraSupplier)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de extra leverancier`,
        23
      );
    }
    if (
      adjustment.needExtraSupplier &&
      adjustment.extraSupplierEmployee == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een medewerker te selecteren voor de extra leverancier`,
        24
      );
    }
    if (
      adjustment.needExtraSupplier &&
      adjustment.extraSupplierDeadline == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een deadline op te geven voor de extra leverancier`,
        25
      );
    }
    if (adjustment.needExtraMaterials == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of extra materialen nodig zijn`,
        26
      );
    }
    if (
      adjustment.needExtraMaterials &&
      StringHelper.isEmpty(adjustment.extraInfoNeedExtraMaterials)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de extra materialen`,
        27
      );
    }
    if (
      adjustment.needExtraMaterials &&
      adjustment.extraMaterialsEmployee == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een medewerker te selecteren voor de extra materialen`,
        28
      );
    }
    if (
      adjustment.needExtraMaterials &&
      adjustment.extraMaterialsDeadline == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een deadline op te geven voor de extra materialen`,
        29
      );
    }
    if (adjustment.inpressLogo == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of het Inpress logo toegevoegd mag worden`,
        30
      );
    }
    if (adjustment.immoscoopLogo == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of het Immoscoop logo toegevoegd mag worden`,
        31
      );
    }
    if (adjustment.spottoLogo == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of het Spotto logo toegevoegd mag worden`,
        31
      );
    }
    if (
      adjustment.type === 'SoldRented' &&
      adjustment.projectPlacement.soldRentedStickerWorkHeight == null
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het veld 'Afstand vanop de grond tot bovenkant sticker' in te vullen`,
        32
      );
    }
    if (adjustment.projectPlacement.heightEquipment == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een keuze te maken voor ladder/stelling/hoogwerker`,
        33
      );
    }
    if (adjustment.projectPlacement.obstaclesPresent == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve aan te geven of er obstakels zijn`,
        34
      );
    }
    if (
      adjustment.projectPlacement.obstaclesPresent &&
      StringHelper.isEmpty(adjustment.projectPlacement.obstaclesInfo)
    ) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve informatie in te vullen over de obstakels`,
        35
      );
    }
    if (!CollectionHelper.isPopulated(adjustment.projectPlacement.placers)) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve minstens één plaatser te selecteren`,
        36
      );
    }
    if (adjustment.currentConfirmedPlacementDate == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve een plaatsingsdatum te kiezen`,
        37
      );
    }
    if (adjustment.projectPlacement.installerHourBudget == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het aantal uren te selecteren voor de voorziene tijd`,
        38
      );
    }
    if (adjustment.projectPlacement.installerMinuteBudget == null) {
      ErrorHelper.evaluateError(
        error,
        `Gelieve het aantal minuten te kiezen voor de voorziene tijd`,
        39
      );
    }
  }

  public locationVerifiedValidationCallback =
    this.locationVerifiedValidation.bind(this);

  compareEntities(o1, o2) {
    return GeneralHelper.compareEntities(o1, o2);
  }

  compareEnums(o1, o2): boolean {
    return GeneralHelper.compareNameEnum(o1, o2);
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}

interface ContactDisplay {
  company: string;
  firstName: string;
  fullName: string;
  phoneNumber?: string;
  mobileNumber?: string;
  email?: string;
}

interface LineWithHeader {
  header: string;
  line: string;
}
